if (!customElements.get('store-locator')) {
  class StoreLocator extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        state: '[data-store-state]',
        city: '[data-store-city]',
        neighborhood: '[data-store-neighborhood]',
        results: '[data-store-results]',
        resultsTitle: '[data-store-results-title]',
        loading: '[data-store-loading]',
      };

      this.stateSelect = this.querySelector(this.selectors.state);
      this.citySelect = this.querySelector(this.selectors.city);
      this.neighborhoodSelect = this.querySelector(this.selectors.neighborhood);
      this.results = this.querySelector(this.selectors.results);
      this.resultsTitle = this.querySelector(this.selectors.resultsTitle);
      this.loading = this.querySelector(this.selectors.loading);

      this.dataUrl = this.dataset.source;
      this.defaultState = this.dataset.defaultState || '';

      this.stores = [];
      this.states = [];
      this.citiesByState = {};
      this.neighborhoodsByStateCity = {};

      this.stateSelect.addEventListener('change', this.onStateChange.bind(this));
      this.citySelect.addEventListener('change', this.onCityChange.bind(this));
      this.neighborhoodSelect.addEventListener('change', this.onNeighborhoodChange.bind(this));
      window.addEventListener('popstate', this.onPopState.bind(this));

      this.fetchStores();
    }

    normalizeSlug(value = '') {
      return value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-');
    }

    getStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const slug = params.get('estado');
      if (!slug) return '';
      return this.states.find((state) => this.normalizeSlug(state) === slug) || '';
    }

    buildUrl(state) {
      const url = new URL(window.location.href);
      if (state) {
        url.searchParams.set('estado', this.normalizeSlug(state));
      } else {
        url.searchParams.delete('estado');
      }
      return url;
    }

    async fetchStores() {
      try {
        const response = await fetch(this.dataUrl);
        const data = await response.json();
        this.stores = Array.isArray(data) ? data : [];
      } catch (error) {
        this.stores = [];
      }

      this.buildIndexes();
      this.populateStateOptions();

      const initialState = this.getStateFromUrl() || this.defaultState || this.states[0] || '';
      this.setState(initialState, { updateHistory: false });
    }

    buildIndexes() {
      this.states = [];
      this.citiesByState = {};
      this.neighborhoodsByStateCity = {};

      const sorted = [...this.stores].sort((a, b) => {
        if (a.estado !== b.estado) return (a.estado || '').localeCompare(b.estado || '', 'pt-BR');
        if (a.cidade !== b.cidade) return (a.cidade || '').localeCompare(b.cidade || '', 'pt-BR');
        return (a.loja || '').localeCompare(b.loja || '', 'pt-BR');
      });

      sorted.forEach((store) => {
        if (store.estado && !this.states.includes(store.estado)) {
          this.states.push(store.estado);
        }

        if (store.estado && store.cidade) {
          if (!this.citiesByState[store.estado]) {
            this.citiesByState[store.estado] = [];
          }
          if (!this.citiesByState[store.estado].includes(store.cidade)) {
            this.citiesByState[store.estado].push(store.cidade);
          }
        }

        if (store.estado && store.cidade && store.bairro) {
          const key = `${store.estado}|${store.cidade}`;
          if (!this.neighborhoodsByStateCity[key]) {
            this.neighborhoodsByStateCity[key] = [];
          }
          if (!this.neighborhoodsByStateCity[key].includes(store.bairro)) {
            this.neighborhoodsByStateCity[key].push(store.bairro);
          }
        }
      });
    }

    populateStateOptions() {
      this.stateSelect.innerHTML = '';

      if (!this.states.length) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = 'Nenhum estado disponível';
        this.stateSelect.appendChild(option);
        this.stateSelect.disabled = true;
        return;
      }

      this.stateSelect.disabled = false;
      this.states.forEach((state) => {
        const option = document.createElement('option');
        option.value = state;
        option.textContent = state;
        this.stateSelect.appendChild(option);
      });
    }

    populateCityOptions(state) {
      this.citySelect.innerHTML = '';

      const allOption = document.createElement('option');
      allOption.value = '';
      allOption.textContent = 'Todas as cidades';
      this.citySelect.appendChild(allOption);

      const cities = (this.citiesByState[state] || []).slice().sort((a, b) => a.localeCompare(b, 'pt-BR'));
      cities.forEach((city) => {
        const option = document.createElement('option');
        option.value = city;
        option.textContent = city;
        this.citySelect.appendChild(option);
      });

      this.citySelect.disabled = cities.length === 0;
    }

    populateNeighborhoodOptions(state, city) {
      this.neighborhoodSelect.innerHTML = '';

      const allOption = document.createElement('option');
      allOption.value = '';
      allOption.textContent = 'Todos os bairros';
      this.neighborhoodSelect.appendChild(allOption);

      let neighborhoods = [];
      if (city) {
        neighborhoods = this.neighborhoodsByStateCity[`${state}|${city}`] || [];
      } else {
        const cities = this.citiesByState[state] || [];
        const set = new Set();
        cities.forEach((c) => {
          (this.neighborhoodsByStateCity[`${state}|${c}`] || []).forEach((n) => set.add(n));
        });
        neighborhoods = [...set];
      }

      neighborhoods = neighborhoods.slice().sort((a, b) => a.localeCompare(b, 'pt-BR'));
      neighborhoods.forEach((neighborhood) => {
        const option = document.createElement('option');
        option.value = neighborhood;
        option.textContent = neighborhood;
        this.neighborhoodSelect.appendChild(option);
      });

      this.neighborhoodSelect.disabled = neighborhoods.length === 0;
    }

    setState(state, { updateHistory = true } = {}) {
      this.currentState = state;
      this.currentCity = '';
      this.currentNeighborhood = '';

      this.stateSelect.value = state;
      this.populateCityOptions(state);
      this.populateNeighborhoodOptions(state, '');
      this.citySelect.value = '';
      this.neighborhoodSelect.value = '';

      if (updateHistory) {
        window.history.pushState({}, '', this.buildUrl(state));
      }

      this.renderResults();
    }

    onStateChange(event) {
      this.setState(event.target.value);
    }

    onCityChange(event) {
      this.currentCity = event.target.value;
      this.currentNeighborhood = '';
      this.populateNeighborhoodOptions(this.currentState, this.currentCity);
      this.neighborhoodSelect.value = '';
      this.renderResults();
    }

    onNeighborhoodChange(event) {
      this.currentNeighborhood = event.target.value;
      this.renderResults();
    }

    onPopState() {
      const state = this.getStateFromUrl() || this.defaultState || this.states[0] || '';
      this.setState(state, { updateHistory: false });
    }

    getFilteredStores() {
      return this.stores.filter((store) => {
        if (this.currentState && store.estado !== this.currentState) return false;
        if (this.currentCity && store.cidade !== this.currentCity) return false;
        if (this.currentNeighborhood && store.bairro !== this.currentNeighborhood) return false;
        return true;
      });
    }

    renderResults() {
      if (this.loading) this.loading.hidden = true;

      const filtered = this.getFilteredStores().sort((a, b) => {
        if (a.cidade !== b.cidade) return (a.cidade || '').localeCompare(b.cidade || '', 'pt-BR');
        return (a.loja || '').localeCompare(b.loja || '', 'pt-BR');
      });

      if (this.resultsTitle) {
        this.resultsTitle.textContent = this.currentState || 'Todas as lojas';
      }

      this.results.innerHTML = '';

      if (!filtered.length) {
        const empty = document.createElement('p');
        empty.className = 'store-locator__empty';
        empty.textContent = 'Nenhuma loja encontrada para esse filtro.';
        this.results.appendChild(empty);
        return;
      }

      filtered.forEach((store) => {
        this.results.appendChild(this.renderCard(store));
      });
    }

    renderCard(store) {
      const card = document.createElement('div');
      card.className = 'store-locator__card';

      const complemento = store.complemento ? ` - ${store.complemento}` : '';
      const bairro = store.bairro || '';
      const estado = store.estado ? ` - ${store.estado}` : '';
      const cidade = store.cidade || '';
      const cep = store.cep ? ` - ${store.cep}` : '';
      const ddd = store.ddd ? `(${store.ddd}) ` : '';
      const telefone = store.telefone ? `${ddd}${store.telefone}` : '';
      const whatsapp = store.whatsapp ? `${ddd}${store.whatsapp}` : '';

      const name = document.createElement('strong');
      name.className = 'store-locator__card-name';
      name.textContent = store.loja || '';
      card.appendChild(name);

      const address = document.createElement('p');
      address.className = 'store-locator__card-address';
      [`${store.endereco || ''}${complemento}`, `${bairro}${cep}`, `${cidade}${estado}`].forEach((line, index) => {
        if (index > 0) address.appendChild(document.createElement('br'));
        address.appendChild(document.createTextNode(line));
      });
      card.appendChild(address);

      if (telefone || whatsapp) {
        const contact = document.createElement('ul');
        contact.className = 'store-locator__card-contact';

        if (telefone) {
          const item = document.createElement('li');
          const line = document.createElement('p');
          line.className = 'store-locator__card-line';
          line.appendChild(this.renderIcon('phone'));
          line.appendChild(document.createTextNode(telefone));
          item.appendChild(line);
          contact.appendChild(item);
        }

        if (whatsapp) {
          const item = document.createElement('li');
          const line = store.link ? document.createElement('a') : document.createElement('p');
          line.className = 'store-locator__card-line';
          if (store.link) {
            line.href = store.link;
            line.target = '_blank';
            line.rel = 'noopener noreferrer';
          }
          line.appendChild(this.renderIcon('whatsapp'));
          line.appendChild(document.createTextNode(whatsapp));
          item.appendChild(line);
          contact.appendChild(item);
        }

        card.appendChild(contact);
      }

      return card;
    }

    renderIcon(type) {
      const icon = document.createElement('i');
      icon.className = 'store-locator__icon store-locator__icon--' + type;
      icon.setAttribute('aria-hidden', 'true');
      return icon;
    }
  }

  customElements.define('store-locator', StoreLocator);
}
